import React, { useState } from 'react'
import { Badge, Box, IconButton, Tooltip } from '@mui/material'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'

import { useNotifications } from '../../hooks/useNotifications'
import NotificationMenu, { type NotificationAnchorConfig } from './NotificationMenu'
import NotificationHistoryDrawer from './NotificationHistoryDrawer'

const BRAND_RED = '#EC1C5A'

// Inspector: drop straight down, right-aligned under the bell.
// Admin: fly out to the RIGHT of the sidebar/rail so the panel never
// overlaps the sidebar/content boundary (MUI clamps to the viewport).
const ANCHOR_CONFIG: Record<'inspector' | 'admin', NotificationAnchorConfig> = {
  inspector: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  },
  admin: {
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  },
}

interface NotificationBellProps {
  /** Drives the dropdown anchor/transform origin. */
  placement?: 'inspector' | 'admin'
  /** Icon tint - the admin rail renders on a dark background ('white'). */
  iconColor?: string
  /** Condensed rail shows a plain dot rather than the unread count. */
  badgeVariant?: 'standard' | 'dot'
  /** One-line account row for the dropdown (admin condensed rail only). */
  accountHeader?: React.ReactNode
}

/**
 * Header bell + unread badge. Owns the dropdown anchor and loads the list
 * lazily on open (the poll only needs the count). The single shared instance
 * is consumed by both the inspector nav and the admin sidebar.
 */
const NotificationBell: React.FC<NotificationBellProps> = ({
  placement = 'inspector',
  iconColor,
  badgeVariant = 'standard',
  accountHeader,
}) => {
  const { notifications, unread, loading, error, loadList, markSeen, markAllSeen } =
    useNotifications()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    loadList()
  }
  const handleClose = () => setAnchorEl(null)
  const handleViewAll = () => {
    setAnchorEl(null)
    setHistoryOpen(true)
  }

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          aria-label={`Notifications, ${unread} unread`}
          sx={{ color: iconColor }}
        >
          <Badge
            badgeContent={unread}
            variant={badgeVariant}
            max={9}
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { backgroundColor: BRAND_RED, color: '#fff' } }}
          >
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <NotificationMenu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        notifications={notifications}
        unread={unread}
        loading={loading}
        error={error}
        onMarkSeen={markSeen}
        onMarkAllSeen={markAllSeen}
        onRetry={loadList}
        onViewAll={handleViewAll}
        anchorConfig={ANCHOR_CONFIG[placement]}
        accountHeader={accountHeader}
      />

      <NotificationHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        unread={unread}
      />
    </>
  )
}

export default NotificationBell
